"use client";

import { Suspense, lazy, useEffect } from "react";

import type { TextContentBlock } from "../../types";
import { cn } from "../../utils";
import { Typewriter } from "../Typewriter";

// ---------------------------------------------------------------------------
// Lazy-load MarkdownContent — it pulls in ReactMarkdown + remark-gfm which
// are heavy dependencies. During streaming we only need the lightweight
// Typewriter component. MarkdownContent is deferred until the output is
// complete and the text needs to be rendered as formatted markdown.
//
// We use React.lazy + Suspense instead of framework-specific dynamic imports
// so this works in both Next.js and Vite.
// ---------------------------------------------------------------------------

const lazyImport = () =>
  import("../MarkdownContent").then((mod) => ({
    default: mod.MarkdownContent,
  }));

const MarkdownContent = lazy(lazyImport);

const formatStandaloneJson = (text: string): string | null => {
  const trimmed = text.trim();

  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);

    if (
      typeof parsed !== "object" ||
      parsed === null ||
      (!Array.isArray(parsed) && Object.keys(parsed).length === 0 && trimmed !== "{}")
    ) {
      return null;
    }

    return JSON.stringify(parsed, null, 2);
  } catch {
    return null;
  }
};

/**
 * Plain-text fallback rendered by Suspense while the MarkdownContent chunk
 * is loading. Matches the visual style of MarkdownContent so there is no
 * layout shift — users see content immediately and markdown formatting
 * materialises once the chunk loads (typically < 200ms on a warm cache).
 */
function PlainTextFallback({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "prose prose-sm max-w-none",
        "leading-relaxed",
        "whitespace-pre-wrap",
        className,
      )}
    >
      <p className="my-1.5 first:mt-0 last:mb-0">{text}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TextBlock
// ---------------------------------------------------------------------------

interface TextBlockProps {
  blocks: TextContentBlock[];
  isStreaming?: boolean;
  className?: string;
}

export const TextBlock = ({
  blocks,
  isStreaming,
  className,
}: TextBlockProps) => {
  const text = blocks.map((b) => b.text).join("");
  const formattedJson = formatStandaloneJson(text);

  // Preload the MarkdownContent chunk as soon as TextBlock mounts (during
  // streaming). By the time streaming finishes and we need MarkdownContent,
  // the chunk will likely already be cached — eliminating the fallback
  // entirely in most cases.
  useEffect(() => {
    lazyImport();
  }, []);

  if (!text) {return null;}

  if (isStreaming) {
    return (
      <div className={cn("text-sm leading-relaxed", className)}>
        <Typewriter content={text} />
      </div>
    );
  }

  if (formattedJson) {
    return (
      <pre
        className={cn(
          "overflow-x-auto rounded-lg bg-zinc-200/60 p-3 text-xs whitespace-pre-wrap",
          className,
        )}
      >
        <code>{formattedJson}</code>
      </pre>
    );
  }

  return (
    <Suspense
      fallback={<PlainTextFallback text={text} className={className} />}
    >
      <MarkdownContent text={text} className={className} />
    </Suspense>
  );
};
